<template>
  <div class="register-container">
    <el-card class="register-card">
      <el-form
        ref="registerFormRef"
        :model="registerForm"
        :rules="rules"
        label-width="120px"
        @submit.prevent="handleRegister"
      >
        <el-form-item label="Логин" prop="username">
          <el-input
            v-model="registerForm.username"
            placeholder="Логин для входа"
            :disabled="authStore.isLoading"
          />
        </el-form-item>

        <el-form-item label="Пароль" prop="password">
          <el-space>
            <el-input
              v-model="registerForm.password"
              type="password"
              placeholder="Введите пароль"
              :disabled="authStore.isLoading"
              show-password
            />
            <el-button type="primary" @click="generateRandomPassword">Сгенерировать</el-button>
          </el-space>
        </el-form-item>

        <el-form-item label="Роль" prop="role">
          <el-select
            v-model="registerForm.role"
            placeholder="Выберите роль"
            :disabled="authStore.isLoading"
          >
            <el-option
              v-for="role in availableRoles"
              :key="role"
              :label="rolesLabels[role]"
              :value="role"
            />
          </el-select>
        </el-form-item>

        <el-form-item>
          <el-button
            type="primary"
            native-type="submit"
            :loading="authStore.isLoading"
            :disabled="authStore.isLoading"
          >
            Создать
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useAuthStore } from '@/stores/auth'
import type { FormInstance, FormRules } from 'element-plus'
import { rolesLabels, UserRole } from '@/types/auth'

const router = useRouter()
const authStore = useAuthStore()
const registerFormRef = ref<FormInstance>()

const registerForm = reactive({
  username: '',
  password: '',
  role: UserRole.CURATOR,
})

const availableRoles = computed(() => {
  if (authStore.user?.role === UserRole.ADMIN) {
    return [UserRole.ADMIN, UserRole.CURATORS_LEAD, UserRole.CURATOR]
  }

  return [UserRole.CURATOR]
})

const rules: FormRules = {
  username: [
    { required: true, message: 'Пожалуйста, введите имя пользователя', trigger: 'blur' },
    { min: 3, message: 'Имя пользователя должно содержать минимум 3 символа', trigger: 'blur' },
    {
      pattern: /^[a-zA-Z0-9]+$/,
      message: 'Логин должен содержать только латинские буквы и цифры',
      trigger: 'blur',
    },
  ],
  password: [
    { required: true, message: 'Пожалуйста, введите пароль', trigger: 'blur' },
    { min: 6, message: 'Пароль должен содержать минимум 6 символов', trigger: 'blur' },
  ],
  role: [{ required: true, message: 'Пожалуйста, выберите роль', trigger: 'change' }],
}

const handleRegister = async () => {
  if (!registerFormRef.value) return

  try {
    await registerFormRef.value.validate()
    await authStore.register(registerForm)

    ElMessageBox.alert(
      `Передайте пользователю данные для входа: <br/>${registerForm.username}<br/>${registerForm.password}`,
      'Успех',
      {
        confirmButtonText: 'Скопировать',
        dangerouslyUseHTMLString: true,
        callback: (e: any) => {
          navigator.clipboard.writeText(`${registerForm.username}\n${registerForm.password}`)
          setTimeout(() => {
            router.push('/')
          }, 5000)
        },
      },
    )
  } catch (error: any) {
    ElMessage.error(error.response?.data?.message || 'Ошибка регистрации')
  }
}

const generateRandomPassword = () => {
  const password = Math.random().toString(36).substring(2, 15)
  registerForm.password = password
}
generateRandomPassword()
</script>

<style scoped>
.register-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.register-card {
  margin-top: 2em;
  width: 100%;
  max-width: 600px;
}

.register-card :deep(.el-card__header) {
  text-align: center;
}

.register-card h2 {
  margin: 0;
  color: #303133;
}
</style>
